import { useState } from 'preact/hooks';
import { platformAuth } from './api';

/**
 * StepUpModal prompts for password re-entry and calls StepUp to unlock
 * step-up-gated capabilities (money mutations, offboard, platform user
 * management, 2FA reset). On success, invokes onSuccess().
 *
 * The 5-minute grant is managed server-side; this component just drives
 * a single re-auth round-trip. Callers should retry the original
 * mutation after onSuccess fires.
 */
export function StepUpModal({
  prompt,
  onSuccess,
  onCancel,
}: {
  prompt: string;
  onSuccess(): void;
  onCancel(): void;
}) {
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: Event) {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      await platformAuth.stepUp(password);
      onSuccess();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Unknown error');
      setSubmitting(false);
    }
  }

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--admin-surface)',
          border: '1px solid var(--admin-border)',
          borderRadius: 12,
          padding: 24,
          width: '100%',
          maxWidth: 420,
        }}
      >
        <h2 style={{ marginTop: 0 }}>Confirm identity</h2>
        <p style={{ color: 'var(--admin-text-dim)', marginBottom: 16 }}>{prompt}</p>
        {err && <div class="error-banner">{err}</div>}
        <form onSubmit={submit}>
          <div class="field">
            <label for="stepup-pw">Password</label>
            <input
              id="stepup-pw"
              type="password"
              value={password}
              autoFocus
              onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
              required
            />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" onClick={onCancel} disabled={submitting}>
              Cancel
            </button>
            <button class="primary" type="submit" disabled={submitting || !password}>
              {submitting ? 'Verifying…' : 'Confirm'}
            </button>
          </div>
        </form>
        <p style={{ fontSize: 12, color: 'var(--admin-text-dim)', marginTop: 16 }}>
          A successful confirmation unlocks sensitive actions for 5 minutes.
        </p>
      </div>
    </div>
  );
}

export class StepUpRequiredError extends Error {
  constructor() {
    super('step-up required');
    this.name = 'StepUpRequiredError';
  }
}

/**
 * runWithStepUp wraps a mutation call. If the server returns a step-up
 * required error, it surfaces a StepUpRequiredError so the caller can
 * prompt the modal and retry.
 */
export async function runWithStepUp<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    if (e instanceof Error && /step-up/i.test(e.message)) {
      throw new StepUpRequiredError();
    }
    throw e;
  }
}
